import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiAward, FiTarget, FiTrendingUp, FiActivity, FiStar, FiZap } = FiIcons;

const MilestoneCard = ({ title, value, year, icon, color, sub }) => (
  <div className="border border-[#658d71] bg-[rgba(0,0,0,0.15)] p-4 flex flex-col items-center text-center relative overflow-hidden group hover:border-[#a9c9b1] transition-colors h-full justify-center">
    <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity">
      <SafeIcon icon={icon} size={64} style={{ color }} />
    </div>
    <SafeIcon icon={icon} size={18} style={{ color }} className="mb-2" />
    <span className="text-[9px] font-black uppercase tracking-widest text-[#658d71] mb-1">{title}</span>
    <span className="text-3xl font-black tabular-nums text-white leading-none">{value}</span>
    {sub && (
      <span className="text-[10px] text-[#a9c9b1] font-bold mt-1 uppercase tracking-widest">
        {sub}
      </span>
    )}
    <span className="text-[9px] font-bold text-[#d8ceb3] mt-2 uppercase tracking-widest bg-[rgba(216,206,179,0.1)] px-2 py-0.5 rounded-sm border border-[rgba(216,206,179,0.3)]">
      {year} Season
    </span>
  </div>
);

const FranchiseMilestones = ({ trends }) => {
  const records = useMemo(() => {
    if (!trends || !trends.length) return null;
    
    // Single Season Records
    const mostWins = trends.reduce((max, t) => (t.wins > max.wins ? t : max), { wins: 0 });
    const mostRuns = trends.reduce((max, t) => (t.runs > max.runs ? t : max), { runs: 0 });
    const mostHR = trends.reduce((max, t) => (t.hr > max.hr ? t : max), { hr: 0 });
    const bestERA = trends.filter(t => t.era > 0).reduce((min, t) => (t.era < min.era ? t : min), { era: 99 });
    const mostSO = trends.reduce((max, t) => (t.so > max.so ? t : max), { so: 0 });
    const highestAvg = trends.reduce((max, t) => (t.avg > max.avg ? t : max), { avg: 0 });
    
    // Derived Records
    const bestWinPct = trends.reduce((max, t) => {
      const total = t.wins + t.losses;
      const pct = total > 0 ? (t.wins / total) : 0;
      return pct > max.pct ? { ...t, pct } : max;
    }, { pct: 0 });

    // Historical Streaks & Jumps
    let currentWinStreak = 0;
    let maxWinStreak = 0;
    let maxWinStreakEndYear = null;

    let maxWinJump = 0;
    let maxWinJumpYear = null;
    let maxWinJumpPrev = null;

    trends.forEach((t, i) => {
      // Consecutive Winning Seasons (> .500)
      if (t.wins > t.losses) {
        currentWinStreak++;
        if (currentWinStreak > maxWinStreak) {
          maxWinStreak = currentWinStreak;
          maxWinStreakEndYear = t.year;
        }
      } else {
        currentWinStreak = 0;
      }

      // Most Improved Season (Year-over-Year Win Jump)
      if (i > 0) {
        const jump = t.wins - trends[i-1].wins;
        if (jump > maxWinJump) {
          maxWinJump = jump;
          maxWinJumpYear = t.year;
          maxWinJumpPrev = trends[i-1].wins;
        }
      }
    });

    return { 
      mostWins, mostRuns, mostHR, bestERA, mostSO, highestAvg, bestWinPct,
      winStreak: { count: maxWinStreak, endYear: maxWinStreakEndYear },
      winJump: { jump: maxWinJump, year: maxWinJumpYear, prev: maxWinJumpPrev, wins: trends.find(t => t.year === maxWinJumpYear)?.wins }
    };
  }, [trends]);

  if (!records) return null;

  return (
    <div className="flex flex-col gap-4">
      
      {/* Streaks & Historical Anomalies */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border border-[#658d71] bg-[#386646] p-4 flex items-center gap-4 relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-10">
            <SafeIcon icon={FiTrendingUp} size={100} color="#d8ceb3" />
          </div>
          <div className="w-12 h-12 rounded-full bg-[rgba(216,206,179,0.2)] border-2 border-[#d8ceb3] flex items-center justify-center shrink-0 z-10">
            <SafeIcon icon={FiStar} size={20} className="text-[#d8ceb3]" />
          </div>
          <div className="z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#a9c9b1]">Franchise Record</p>
            <p className="text-lg font-black uppercase text-white leading-tight mt-0.5">
              {records.winStreak.count} Consecutive Winning Seasons
            </p>
            <p className="text-[10px] font-bold text-[#d8ceb3] uppercase tracking-widest mt-1">
              Ended in {records.winStreak.endYear}
            </p>
          </div>
        </div>

        <div className="border border-[#658d71] bg-[#386646] p-4 flex items-center gap-4 relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-10">
            <SafeIcon icon={FiZap} size={100} color="#a9c9b1" />
          </div>
          <div className="w-12 h-12 rounded-full bg-[rgba(169,201,177,0.2)] border-2 border-[#a9c9b1] flex items-center justify-center shrink-0 z-10">
            <SafeIcon icon={FiZap} size={20} className="text-[#a9c9b1]" />
          </div>
          <div className="z-10">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#a9c9b1]">Most Improved Season</p>
            <p className="text-lg font-black uppercase text-white leading-tight mt-0.5">
              +{records.winJump.jump} Wins in {records.winJump.year}
            </p>
            <p className="text-[10px] font-bold text-[#d8ceb3] uppercase tracking-widest mt-1">
              Jumped from {records.winJump.prev} to {records.winJump.wins} Wins
            </p>
          </div>
        </div>
      </div>

      {/* Single Season Records Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MilestoneCard title="Most Wins" value={records.mostWins.wins} year={records.mostWins.year} icon={FiAward} color="#d8ceb3" sub={`${records.mostWins.losses} Losses`} />
        <MilestoneCard title="Best Win %" value={(records.bestWinPct.pct * 100).toFixed(1) + '%'} year={records.bestWinPct.year} icon={FiAward} color="#d8ceb3" sub={`${records.bestWinPct.wins}-${records.bestWinPct.losses} Record`} />
        <MilestoneCard title="Most Runs" value={records.mostRuns.runs} year={records.mostRuns.year} icon={FiTrendingUp} color="#a9c9b1" />
        <MilestoneCard title="Most HRs" value={records.mostHR.hr} year={records.mostHR.year} icon={FiTarget} color="#e85555" />
        <MilestoneCard title="Highest AVG" value={records.highestAvg.avg.toFixed(3).replace('0.', '.')} year={records.highestAvg.year} icon={FiActivity} color="#d8ceb3" />
        <MilestoneCard title="Lowest ERA" value={records.bestERA.era.toFixed(2)} year={records.bestERA.year} icon={FiActivity} color="#a9c9b1" />
        <MilestoneCard title="Strikeouts" value={records.mostSO.so} year={records.mostSO.year} icon={FiTarget} color="#e85555" />
        <div className="border border-[rgba(101,141,113,0.4)] bg-[rgba(0,0,0,0.1)] p-4 flex flex-col items-center justify-center text-center">
          <span className="text-[9px] font-black uppercase tracking-widest text-[#658d71] mb-1">Franchise History</span>
          <span className="text-xl font-black text-[#a9c9b1] uppercase">Records</span>
          <span className="text-[10px] font-bold text-[#658d71] mt-2 uppercase tracking-widest">Since 1901</span>
        </div>
      </div>
    </div>
  );
};

export default FranchiseMilestones;